/**
 * STATIC CONTENT — src/lib/staticContent.ts
 *
 * ALL user-facing static strings that rotate or vary must live here.
 * DO NOT create local arrays in components for tips, phrases, or insights.
 *
 * FRESHNESS RULE: Review and update this file every 2 months or when
 * new scientific facts are validated in the library (7000+ facts).
 * To update: add items to the arrays below. Never remove existing items
 * (users may have seen them — removing creates perception of repetition).
 *
 * LAST REVIEWED: April 2026
 * NEXT REVIEW DUE: June 2026
 *
 * @since Optimization Audit v1
 */

export const MOTIVATIONAL_PHRASES: string[] = [
  "Chaque répétition te rapproche de ton objectif.",
  "La constance est le vrai secret des champions.",
  "Ton corps peut le supporter — ton mental doit le vouloir.",
  "Le progrès est silencieux, mais il est réel.",
  "Pas de raccourci. Juste du travail bien fait.",
  "Hier tu as dit demain. Aujourd'hui, agis.",
  "Ton futur toi te remerciera.",
  "1% de mieux chaque jour = 37x en un an.",
  "La discipline bat la motivation, toujours.",
  "Le repos fait partie de l'entraînement.",
  "Chaque séance est un investissement dans ta santé.",
  "Tu n'as pas besoin d'être parfait. Juste régulier.",
  "Le mouvement est la meilleure médecine.",
  "Fais confiance au processus.",
  "Le seul mauvais entraînement est celui que tu n'as pas fait.",
  "Ton corps s'adapte — laisse-lui le temps.",
  "Force, patience, régularité. Les 3 piliers.",
  "Aujourd'hui est un bon jour pour progresser.",
  "Chaque pas compte, même les plus petits.",
  "La performance vient avec la persévérance.",
];

export const DAILY_TIPS: string[] = [
  "Les séances de 45min sont aussi efficaces que celles d'1h30 si l'intensité est bonne.",
  "Le sommeil construit le muscle. 8h = meilleure récupération.",
  "La régularité bat l'intensité. 3x/semaine pendant 6 mois > 6x/semaine pendant 1 mois.",
  "L'échauffement réduit le risque de blessure de 50% selon les études.",
  "La créatine est le complément le plus étudié et validé par la science.",
  "20g de protéines post-entraînement suffisent pour maximiser la synthèse musculaire.",
  "Le stress chronique augmente le cortisol et freine la récupération musculaire.",
  "Marcher 10 minutes après un repas améliore la glycémie de 30%.",
  "La déshydratation de 2% réduit les performances de 10 à 20%.",
  "Les étirements statiques avant l'effort réduisent la force. Préfère le dynamique.",
  "L'entraînement excentrique est le plus efficace pour la force et la prévention.",
  "La variabilité cardiaque (HRV) est un meilleur indicateur de récupération que le ressenti.",
  "La caféine améliore la performance si consommée 30-60 min avant l'effort.",
  "Le froid post-effort réduit l'inflammation mais peut freiner l'hypertrophie.",
  "La charge progressive est le facteur n°1 de progression à long terme.",
  "Les micro-pauses de 30s entre séries augmentent le volume total supportable.",
  "Le magnésium est impliqué dans 300+ réactions enzymatiques liées à la performance.",
  "L'entraînement en zone 2 (cardio léger) améliore la base aérobie sans fatigue excessive.",
  "Les exercices unilatéraux corrigent les déséquilibres musculaires.",
  "La visualisation mentale améliore la performance de 10 à 15% selon les études.",
  "Une bonne hydratation commence la veille, pas le jour de l'entraînement.",
  "Les glucides sont le carburant n°1 pour les efforts de haute intensité.",
  "Le tempo contrôlé (3-1-2) maximise le temps sous tension musculaire.",
  "La respiration nasale à l'effort réduit le stress et améliore l'endurance.",
  "Les exercices composés recrutent plus de fibres musculaires que l'isolation.",
  "Le surentraînement commence quand la récupération est insuffisante, pas quand le volume est élevé.",
  "Les protéines végétales sont aussi efficaces si les sources sont variées.",
  "Un échauffement spécifique au mouvement est supérieur à un échauffement général.",
  "La sieste de 20 min améliore la vigilance et la performance sportive.",
  "La progression n'est pas linéaire — les plateaux sont normaux et temporaires.",
];

export const RECOVERY_TIPS: string[] = [
  "Dors au moins 7h pour optimiser ta récupération hormonale.",
  "Alterne chaud et froid pour accélérer la récupération musculaire.",
  "L'auto-massage avec un rouleau réduit les courbatures de 30%.",
  "Hydrate-toi régulièrement — pas seulement pendant l'entraînement.",
  "Le yoga ou la mobilité active accélèrent la récupération.",
  "Écoute ton corps : une douleur persistante mérite une pause.",
  "Les jours de repos actif (marche, natation légère) sont plus efficaces que l'inactivité totale.",
  "Le stress mental impacte la récupération autant que le stress physique.",
  "Les oméga-3 réduisent l'inflammation post-effort.",
  "La compression post-effort peut réduire les dommages musculaires.",
];

export const BRAIN_INSIGHTS: string[] = [
  "Le Cerveau a analysé 7 000+ faits scientifiques pour tes recommandations.",
  "Tes programmes sont adaptés à ton profil morphologique et tes objectifs.",
  "Le Cerveau croise tes données de sommeil et d'entraînement pour optimiser ta charge.",
  "La périodisation de ton programme est basée sur des études peer-reviewed.",
  "Le Cerveau surveille tes signaux de fatigue pour prévenir le surentraînement.",
  "Chaque exercice est sélectionné en fonction de ton historique de blessures.",
  "Le Cerveau adapte l'intensité en fonction de ta progression réelle.",
  "Les temps de repos sont calculés selon ta capacité de récupération.",
  "Le Cerveau identifie les déséquilibres musculaires dans tes programmes.",
  "Tes apports nutritionnels sont calibrés sur ton métabolisme et tes objectifs.",
  "Le Cerveau détecte les patterns de surcharge articulaire.",
  "La sélection d'exercices tient compte de tes préférences et de la science.",
  "Le Cerveau ajuste la périodisation selon ta réponse à l'entraînement.",
  "Tes cycles de travail/repos sont optimisés par les données de la communauté.",
  "Le Cerveau évalue la qualité de chaque séance, pas juste la quantité.",
];

export const POST_CHECKIN_PHRASES: string[] = [
  "Bien joué ! Chaque jour compte dans ta progression. 💪",
  "Check-in enregistré — le Cerveau a pris note. 🧠",
  "Régularité validée. Continue comme ça !",
  "Ta constance est ta plus grande force.",
  "Donnée enregistrée — ta courbe de progression se dessine.",
  "Le Cerveau ajuste tes recommandations en temps réel.",
  "Beau travail ! Chaque signal compte pour ta progression.",
  "Journée notée. Le Cerveau optimise la suite.",
  "Ta discipline paie — les données le confirment.",
  "Check-in reçu. Le prochain entraînement sera encore mieux calibré.",
];

export const COMMUNITY_FEATURED_TIPS: string[] = [
  "Les séances de 45min sont aussi efficaces que celles d'1h30 si l'intensité est bonne.",
  "Le sommeil construit le muscle. 8h = meilleure récupération.",
  "La régularité bat l'intensité. 3x/semaine pendant 6 mois > 6x/semaine pendant 1 mois.",
  "L'échauffement réduit le risque de blessure de 50% selon les études.",
  "La créatine est le complément le plus étudié et validé par la science.",
  "20g de protéines post-entraînement suffisent pour maximiser la synthèse musculaire.",
  "Le stress chronique augmente le cortisol et freine la récupération musculaire.",
  "Marcher 10 minutes après un repas améliore la glycémie de 30%.",
  "La déshydratation de 2% réduit les performances de 10 à 20%.",
  "Les étirements statiques avant l'effort réduisent la force. Préfère le dynamique.",
  "L'entraînement excentrique est le plus efficace pour la force et la prévention.",
  "La variabilité cardiaque (HRV) est un meilleur indicateur de récupération que le ressenti.",
];

// --- Helpers ---

/** Deterministic daily rotation from an array */
export function getDailyItem<T>(arr: T[]): T {
  return arr[new Date().getDate() % arr.length];
}

/** Seeded rotation from an array */
export function getSeededItem<T>(arr: T[], seed: number): T {
  return arr[Math.abs(seed) % arr.length];
}

// --- DEV: array size validator ---
if (import.meta.env.DEV) {
  const MINIMUMS: Record<string, number> = {
    DAILY_TIPS: 30,
    COMMUNITY_FEATURED_TIPS: 20,
    POST_CHECKIN_PHRASES: 15,
    BRAIN_INSIGHTS: 20,
    RECOVERY_TIPS: 10,
    MOTIVATIONAL_PHRASES: 20,
  };

  const arrays: Record<string, readonly string[]> = {
    DAILY_TIPS,
    COMMUNITY_FEATURED_TIPS,
    POST_CHECKIN_PHRASES,
    BRAIN_INSIGHTS,
    RECOVERY_TIPS,
    MOTIVATIONAL_PHRASES,
  };

  Object.entries(MINIMUMS).forEach(([name, min]) => {
    const arr = arrays[name];
    if (!arr || arr.length < min) {
      console.warn(
        `[PERFORM staticContent] ${name} has ${arr?.length ?? 0} items ` +
        `— minimum recommended: ${min}. Add more to avoid repetition.`
      );
    }
  });
}
